import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { applyFilters } from 'modules/core/lib/filters'
import FiltersForm from 'modules/core/lib/forms/Filters'

import type Employee from 'modules/employees/store/models/Employee'
import SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import getFilterValue from 'modules/payroll_bundle/components/Supplements/components/Table/_filters/getFilterValue'
import type { RouteContextParams } from 'modules/payroll_bundle/lib/RouteContextParams'
import { INITIAL_PAGE, PAGINATION_PER_PAGE_SUPPLEMENTS } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { SupplementsInsightsContextProvider } from 'modules/payroll_bundle/pages/SupplementsPage/supplementsInsightsContext'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { ExpensablesProvider } from '../expensablesContext'
import { SupplementExtraDataProvider } from '../supplementExtraDataContext'
import useFetchSupplementsBatchByPeriod from '../useFetchSupplementsBatchByPeriod'
import SearchableSupplements from './SearchableSupplements'

interface SupplementsProps {
  policyPeriod?: PolicyPeriod | null
  bulkImportSupplementStage?: string
  itemToHighlight?: { employeeId: number } | null
}

interface FetchParams {
  policyPeriodId: number | null | undefined
  employeeIds: number[]
}

export const SupplementsByPeriod = observer((props: SupplementsProps) => {
  const supplementsStore = useLazyRef<SupplementsFormStore>(
    () => new SupplementsFormStore()
  ).current

  const filtersForm = useLazyRef<FiltersForm>(
    () =>
      new FiltersForm({
        sortAttribute: 'first_name',
        sortDirection: 'asc',
        filterBy: 'activity___all',
      })
  ).current

  const { search, onSearch, debouncedSearch } = useDebouncedSearch()
  const { currentPolicyPeriods, periodIndex } = usePolicyPeriods()
  const { filteredEmployees: employeesInPeriod } = useOutletContext<RouteContextParams>()

  const [fetchParams, setFetchParams] = useState<FetchParams>({
    policyPeriodId: currentPolicyPeriods[periodIndex]?.id,
    employeeIds: [],
  })

  const visibleEmployees = employeesInPeriod.filter(
    (employee) =>
      policyAuthorizations.for('payroll_supplements.supplements.read', employee) ||
      policyAuthorizations.for('payroll_supplements.observations.read', employee)
  )

  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE)

  const employeesInPeriodIds = visibleEmployees
    .map((employee) => employee.id)
    .sort((a, b) => a - b)
    .join('-')

  useEffect(() => {
    onApplyFilters(visibleEmployees)
    setCurrentPage(INITIAL_PAGE)
  }, [employeesInPeriodIds]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    filteredModels: filteredEmployeesModels,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, LOCAL_STORAGE_KEYS.supplementFilters)

  const { loading: loadingFilteredEmployees, value: filteredEmployees } = useAsyncData(
    () =>
      applyFilters(filtersForm, filteredEmployeesModels, {
        search: debouncedSearch,
      }),
    [
      filtersForm.get('filterBy').value,
      filtersForm.get('sortDirection').value,
      filtersForm.get('sortAttribute').value,
      filteredEmployeesModels.length,
      debouncedSearch,
    ]
  )

  const employeeIds: number[] = filteredEmployees?.map((employee) => employee.id) ?? []

  const policyPeriodId = currentPolicyPeriods[periodIndex]?.id

  const slicedEmployeeIds = useMemo(
    () =>
      employeeIds.slice(
        PAGINATION_PER_PAGE_SUPPLEMENTS * (currentPage - 1),
        PAGINATION_PER_PAGE_SUPPLEMENTS * currentPage
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employeeIds.join('-'), currentPage]
  )

  useEffect(() => {
    setFetchParams({
      policyPeriodId,
      employeeIds: slicedEmployeeIds,
    })
  }, [policyPeriodId, slicedEmployeeIds.join('-')])

  const { loading } = useFetchSupplementsBatchByPeriod({
    policyPeriodId: fetchParams.policyPeriodId,
    supplementsStore,
    employeeIds: fetchParams.employeeIds,
  })

  return (
    <SupplementsInsightsContextProvider>
      <SupplementExtraDataProvider
        supplementsStore={supplementsStore}
        loadingSupplementData={loading}
      >
        <ExpensablesProvider supplementsStore={supplementsStore}>
          <SearchableSupplements
            {...props}
            store={supplementsStore}
            loading={loading || loadingFilteredEmployees}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            filteredEmployees={filteredEmployees ?? []}
            onSearch={onSearch}
            search={search}
            filteredEmployeesModels={filteredEmployeesModels}
            logicOperator={logicOperator}
            filters={filters}
            onApplyFilters={onApplyFilters}
            onCancel={onCancel}
            onRemoveFilter={onRemoveFilter}
            onAddFilter={onAddFilter}
          />
        </ExpensablesProvider>
      </SupplementExtraDataProvider>
    </SupplementsInsightsContextProvider>
  )
})
